import React, { Component } from 'react';
import { isEqual } from 'lodash';
import styled, { withTheme } from 'styled-components';
import PropTypes from 'prop-types';
import { AbstractChart } from 'containers/charts/AbstractChart';
import { formatNumbers } from 'helpers/formatNumbers';
import ContentTile from 'components/atoms/layout/ContentTile';
import ContentHeader from 'components/atoms/layout/ContentHeader';
import helpers from 'helpers';


const ChartWrapper = styled.div`
    width: 100%;
    margin-top: 5px;
    overflow: hidden;
    position: relative;
`;

const TotalWrapper = styled.div`
    position: absolute;
    width: calc(100% - ${props => props.legendWidth});
    height: ${props => props.chartHeight};
    left: 0px;
    color:  ${props => props.theme.charts.titleColor};
    display: ${props => (props.chartWidth ? 'flex' : 'none')};
    overflow: hidden;
    flex-direction: column;
    pointer-events: none;
    justify-content: center;
    align-items: center;
    margin-left: ${props => props.legendWidth};
`;

const TotalValue = styled.div`
    font-size: 22px;
`;

const TotalTitle = styled.div`
    font-size: 14px;
    letter-spacing: 1px;
`;

class DrillDownDonut extends Component {
    constructor(props) {
        super(props);
        this.state = {
            chartWidth: null,
            chartHeight: null,
            legendWidth: null
        };

        this.adjustTotalsWidth = this.adjustTotalsWidth.bind(this);
    }

    adjustTotalsWidth(chart) {
        this.setState({
            chartWidth: `${chart.target.plotBox.width}px`,
            chartHeight: `${chart.target.plotBox.height}px`,
            legendWidth: `${chart.target.legend.legendWidth + 10}px` // 10 is for the margin between the legend and the chart
        });
    }

    shouldComponentUpdate(nextProps, nextState) {
        return !isEqual(nextProps.data, this.props.data) ||
            !isEqual(nextProps.total, this.props.total) ||
            !isEqual(nextState.chartWidth, this.state.chartWidth);
    }

    render() {
        const { orderedColors } = this.props.theme;
        const { data, title, total, totalFormat, y, donutCaption } = this.props;
        const { bucketPercentage } = this.props;
        const bucketFloat = bucketPercentage / 100 || null;
        let drillDownEnabled = false;
        const totalTitle = String(this.props.totalTitle).toUpperCase();
        const totalValue = formatNumbers(total, totalFormat);
        const cssClassSuffix = title.replace(/ /g, '-').toLowerCase();
        let bucketData = [];
        let bucketTotal = 0;
        let seriesData = data.map((element, index) => {
            // ensure colors are added
            if (!element.color) {
                element.color = orderedColors[index % orderedColors.length];
            }
            return element;
        });

        if (bucketPercentage) {
            seriesData = seriesData.filter((element) => {
                if (element.y > bucketFloat) {
                    return true;
                }
                // form drillDownData
                bucketData.push({
                    name: element.name,
                    y: element.y
                });
                bucketTotal += element.y;
                return false;
            });

            drillDownEnabled = bucketData.length > 0;

            if (drillDownEnabled) {
                seriesData.push({
                    name: 'Other',
                    y: bucketTotal,
                    drilldown: 'other',
                    color: '#A0A0A0'
                });
                // rank and add colors to drillDownData
                bucketData = Object.values(bucketData).sort((a, b) => b.y - a.y)
                    .map((element, index) => {
                        element.color = orderedColors[index % orderedColors.length];
                        return element;
                    });
            }
        }

        const chartConfig = {
            meta: {
                title: this.props.title,
                totalValue,
                totalTitle
            },
            title: {
                text: null
            },
            chart: {
                type: 'pie',
                plotBackgroundColor: null,
                backgroundColor: 'rgba(255, 255, 255, 0.0)',
                plotBorderWidth: null,
                plotShadow: false,
                style: {
                    fontFamily: 'Lato'
                },
                events: {
                    load: this.adjustTotalsWidth,
                    redraw: this.adjustTotalsWidth,
                },
            },
            exporting: helpers.commonExportingConfig(this.props.exportName),
            plotOptions: {
                pie: {
                    size: '100%',
                    innerSize: '65%',
                    showInLegend: true,
                    allowPointSelect: true,
                    dataLabels: {
                        enabled: false,
                        style: {
                            fontSize: '10px',
                            textOutline: false,
                            color: this.props.theme.colors.darkgray
                        }
                    },
                    events: {
                        afterAnimate() {
                            const chart = this.chart;
                            const legend = chart.legend;
                            const tooltip = this.chart.tooltip;
                            legend.allItems.forEach((item) => {
                                item.legendItem.on('mouseover', () => {
                                    const tooltipData = item.series.data[item.index];
                                    tooltip.refresh(tooltipData);
                                }).on('mouseout', () => {
                                    tooltip.hide();
                                });
                            });
                        }
                    }
                }
            },
            tooltip: {
                pointFormat: '<b>{point.percentage:.2f}%</b>'
            },
            credits: {
                enabled: false
            },
            legend: {
                layout: 'vertical',
                align: 'left',
                verticalAlign: 'top',
                itemStyle: {
                    color: this.props.theme.colors.darkgray,
                    fontSize: '12px'
                },
                itemWidth: 250
            },
            responsive: {
                rules: [{
                    condition: {
                        maxWidth: 700,
                    },
                    chartOptions: {
                        legend: {
                            itemStyle: {
                                fontSize: '10px'
                            },
                            itemWidth: 160
                        },
                        plotOptions: {
                            pie: {
                                dataLabels: {
                                    enabled: false
                                }
                            }
                        },
                    }
                }, {
                    condition: {
                        maxWidth: 400,
                    },
                    chartOptions: {
                        legend: {
                            itemStyle: {
                                fontSize: '10px'
                            },
                            itemWidth: 100
                        },
                    }
                }]
            },
            series: [{
                name: `${helpers.convertToDisplayValue(y)}`,
                data: seriesData
            }],
            caption: {
                text: donutCaption
            }
        };

        if (drillDownEnabled) {
            chartConfig.drilldown = {
                series: [{
                    id: 'other',
                    data: bucketData
                }]
            };
        }

        return (
            <ContentTile>
                <ContentHeader marginBottom="0px" className={`contentHeader-super-donut-${cssClassSuffix}`}>{title}</ContentHeader>
                <ChartWrapper>
                    <TotalWrapper
                        chartWidth={this.state.chartWidth}
                        chartHeight={this.state.chartHeight}
                        legendWidth={this.state.legendWidth}
                    >
                        <TotalValue>{totalValue}</TotalValue>
                        <TotalTitle>{totalTitle}</TotalTitle>
                    </TotalWrapper>
                    <AbstractChart exportName={this.props.exportName} config={chartConfig} />
                </ChartWrapper>
            </ContentTile>
        );
    }
}

DrillDownDonut.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        y: PropTypes.number.isRequired,
        color: PropTypes.string
    })).isRequired,
    title: PropTypes.string.isRequired,
    total: PropTypes.number.isRequired,
    totalTitle: PropTypes.string.isRequired,
    totalFormat: PropTypes.string.isRequired,
    y: PropTypes.string.isRequired
};

export default withTheme(DrillDownDonut);



// WEBPACK FOOTER //
// ./src/components/molecules/charts/DrillDownDonut.js